import { initializeScrollManager, destroyScrollManager, initializeLenisAnchorScrolling, handleHashScroll, getLenisInstance } from './core/scroll-manager.js';
import { initializeScrollProgressIndicators, destroyScrollProgressIndicators, initScrollPercentElements, destroyScrollPercentElements, initScrollVarElements, destroyScrollVarElements, refreshScrollElements } from './core/scroll-utils.js';
import { initializeScrollEffects, destroyScrollEffects, refreshScrollEffects, registerScrollCall, resetContainerAnimations } from './core/scroll-effects.js';

import { initializeParallax, destroyParallax } from './core/parallax.js';
import { initializeVideoCoverResizerListeners, resizeIframesToCover } from './core/video-cover-resizer.js';
import { querystringDragging } from './core/url-utils.js';
import { initializeDomLoadListeners } from './core/dom-load-listeners.js';
import { matchElementHeight } from './core/match-element-height.js';
import { loadWebFonts } from './core/web-fonts.js';
import { updateNavMenuClasses } from './core/nav-menu-updater.js';
import { trackPageView } from './vendor/google-analytics.js';
import { initializeInteractiveHeader } from './ui/header/interactive-header.js';
import { initializeMobileMenu } from './ui/header/mobile-menu.js';
import { initializeStickyHeader, destroyStickyHeader } from './ui/header/sticky.js';
// import { initializeEnquireButton } from './ui/header/enquire-button.js'; // disabled — no Enquire CTA in current design
import { initializeVideoPlayers, destroyVideoPlayers } from './ui/video/slideshow-loader.js';
import { slideshows, destroySlideshows } from './ui/slideshow-manager.js';
import { barbaPreventHandler, syncManualBodyClasses, syncPageStyles, syncPageStylesheets, syncPageScriptsByLocation, syncPageModals, fadeIn, fadeOut } from './vendor/barba.js';
import { initializeVideoPopupHandler } from './ui/video/modal-video-loader.js';
import { googleMaps } from './integrations/google-maps-manager.js';
import { initializeModals, destroyModals } from './ui/modal-manager.js';
import { initializeImageZoom } from './ui/image-zoom.js';
import { initializeFauxSelects } from './ui/faux-select.js';
import { initializeAccordions, destroyAccordions } from './ui/accordion.js';
import { initializeTabs, destroyTabs } from './ui/tabs.js';
import { initializeReadReveal, initializeReadRevealListeners } from './ui/read-reveal.js';
//import { initializeQuickSearch } from './ui/quick-search.js';
//import { initializePropertiesMap, destroyPropertiesMap } from './features/properties-map.js';
import { initializeCountUp, destroyCountUp } from './features/count-up.js';
import { initializeHeroTaglines, destroyHeroTaglines } from './features/hero-taglines.js';
import { destroyHeroScrollOpacity } from './features/hero-scroll-opacity.js';
import { initializeAlmFeatures } from './custom/alm-features.js';
import { initializeFaqs } from './custom/faqs.js';
import { initializeObfuscatedLinks } from './core/obfuscate-links.js';
import { initializeScrollToTop, destroyScrollToTop } from './custom/button-scroll-to-top.js';
import { initializeMenuBg, destroyMenuBg } from './custom/menu-bg.js';
import { resetAjaxLoadMore, reinitializeAjaxLoadMore } from './integrations/ajax-load-more-manager.js';
import { refreshGravityForms } from './integrations/gravity-forms-manager.js';
import { monitorMediaLoading, resetPageLoaderProgress, updatePageLoaderProgress } from './core/page-loader.js';
import MicroModal from 'micromodal';
import barba from '@barba/core';
// Optional: If you use CSS classes for transitions via @barba/css
// import barbaCss from '@barba/css';

// --- Global Settings ---
const DEBUG_MODE = process.env.WP_ENVIRONMENT_TYPE === 'production';

// --- History API Proxy ---
// Patch pushState and replaceState to ensure Barba's state is preserved
// when other scripts (like ALM Filters) try to overwrite it.
(function() {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(state, title, url) {
        const currentState = history.state || {};
        const newState = Object.assign({}, currentState, state);
        return originalPushState.apply(this, [newState, title, url]);
    };

    history.replaceState = function(state, title, url) {
        const currentState = history.state || {};
        const newState = Object.assign({}, currentState, state);
        return originalReplaceState.apply(this, [newState, title, url]);
    };
})();

// Configuration for querystringDragging
const querystringDraggingConfig = {
    domains: [
        window.location.hostname, // Automatically include the current site's domain
        // 'another-site.com.au', // Example: Add other domains if you cross-link and want to decorate them
    ],
    params: ['utm_medium', 'utm_source', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid']
};

// --- Web Font Configuration ---
// Define your font families here. These will be passed to the web-fonts loader.
// !!!NOTE!!! Use the family name and not the font name
const selfHostedFontConfig = [
    { family: 'TT Norms Pro Mono', weight: '500', style: 'normal' },
    { family: 'Aeonik', weight: '400', style: 'normal' },
];

const adobeFontConfig = [
  // { family: 'adobe-font-family-one' }, // Observer will try to load any weight/style
];

// --- Global State ---
let lenisInstance; // Keep a single Lenis instance across page transitions
let footerSpacerInstance; // Store instance for footer height matching


/**
 * Initializes all page-specific JavaScript that needs to run after
 * new content is loaded by Barba.js.
 * @param {object} data - Barba transition data object.
 */
function initializePageScripts(data) {
    if (!data || !data.next) return;
    if (DEBUG_MODE) console.log('Barba: Initializing scripts for new page:', data.next.namespace);

    // Update nav menu classes for the new page
    if (data.next.url && data.next.url.href) {
        updateNavMenuClasses(data.next.url.href, ['.header__primary', '.header__corporate-nav', '.footer__corporate']);
    }

    // The Lenis instance is now persistent. We just need to re-initialize components that use it.
    if (lenisInstance) {
        initializeScrollProgressIndicators('[data-js-scroll-distance="1"]', lenisInstance);
        initScrollPercentElements(lenisInstance);
        initScrollVarElements(lenisInstance);
        initializeLenisAnchorScrolling(lenisInstance); // Re-attaches click listeners if needed, though a single global one is better.
        const hash = data.next && data.next.url ? data.next.url.hash : null;
        handleHashScroll(lenisInstance, hash); // Explicitly handle hash scrolling after transition
        initializeStickyHeader(lenisInstance);
    }

    // Initialize scroll-triggered effects like class toggles and background color changes.
    initializeScrollEffects();

    // Initialize parallax effects for elements with the .parallax class
    initializeParallax();

    // Initialize modals for the new page
    initializeModals(DEBUG_MODE);

    // Decorate links with query parameters
    querystringDragging(
        querystringDraggingConfig.domains,
        querystringDraggingConfig.params,
        DEBUG_MODE
    );

    // IMPORTANT: Re-initialize other plugins and custom scripts here.
    // This is highly specific to your theme and plugins.
    // Examples:
    // if (typeof AOS !== 'undefined') { AOS.refreshHard(); }
    // if (jQuery && jQuery.fn.slick) { jQuery('.my-slider', data.next.container).slick(); }
    // if (window.myCustomGalleryInit) { window.myCustomGalleryInit(data.next.container); }

    // Apply 'cover' resizing to any video embeds on the new page.
    resizeIframesToCover('.video-container', data.next.container);

    // Initialize faux select boxes for the new page content
    initializeFauxSelects(data.next.container);

    // Initialize accordions for the new page content
    initializeAccordions(data.next.container);

    // Initialize tabs for the new page content
    initializeTabs(data.next.container);

    // Initialize read reveal for the new page content
    initializeReadReveal(data.next.container);

    // Initialize quick search logic for the new page content
    //initializeQuickSearch(data.next.container);

    // Initialize video players for fade-in effect.
    initializeVideoPlayers(data.next.container);

    // Initialize Properties Map (if present on page)
    //initializePropertiesMap();

    // Initialize CountUp animations
    initializeCountUp();

    // Initialize Hero Taglines
    initializeHeroTaglines();

    // Initialize Hero Scroll Opacity
    //initializeHeroScrollOpacity(data.next.container);

    initializeAlmFeatures();
    initializeFaqs(data.next.container);

    // Initialize Scroll to Top button for new pages
    initializeScrollToTop(lenisInstance);

    // Initialize Menu Background scroll progress
    initializeMenuBg(lenisInstance, data.next.container);

    // Initialize Obfuscated Links
    initializeObfuscatedLinks(data.next.container);

    // Initialize Footer Spacer using generic helper
    //if (footerSpacerInstance) footerSpacerInstance.destroy();
    //footerSpacerInstance = matchElementHeight('.footer', '.footer-spacer');

    // --- Slideshow Initialization ---
    // Get page-specific slideshow configurations from data passed via inline script
    const pageSlideshowConfigs = window.pageSpecificData?.slideshows || [];

    // Initialize Slideshows if configs exist
    // We call this unconditionally now because it handles WP galleries and other global slideshows
    // that don't rely on pageSlideshowConfigs.
    // Scope DOM-driven lookups to the incoming container: during a Barba sync transition the outgoing
    // and incoming containers coexist, so a document-wide query can match a stale, still-initialized
    // slideshow in the old container and skip initializing the new one.
    slideshows(pageSlideshowConfigs, DEBUG_MODE, data.next.container);

    // --- Google Maps Initialization ---
    // Prepare global settings from localized data
    const googleMapsGlobalSettings = {
        apiKey: window.app?.settings?.global_google_maps_api_key || '',
        mapId: window.app?.settings?.global_google_maps_map_id || '',
        // Safely parse styles, defaulting to an empty array
        defaultStyles: JSON.parse(window.app?.settings?.googleMapsDefaultSettings?.stylesString || '[]'),
        defaultMarker: window.app?.settings?.googleMapsDefaultSettings?.marker || null,
        // Configuration for resizing map markers on different screen sizes.
        responsiveMarkerConfig: {
            // Breakpoints should match your SCSS variables.
            breakpoints: {
                xs: 640,
                md: 1080,
            },
            // Scaling factors for markers at each breakpoint.
            scales: {
                large: 1.0,   // Desktop (original size)
                medium: 0.7, // Tablet
                small: 0.7    // Mobile
            }
        }
    };

    // Get page-specific map configurations from data passed via inline script
    const pageMapConfigs = window.pageSpecificData?.google_maps_page || {};

    // Initialize Google Maps if an API key is present
    if (googleMapsGlobalSettings.apiKey) {
        googleMaps(pageMapConfigs, googleMapsGlobalSettings, DEBUG_MODE);
    } else if (DEBUG_MODE && document.querySelector('.google-map__canvas') && !googleMapsGlobalSettings.apiKey) {
        console.warn('Google Maps: API key is missing from global settings. Maps will not be initialized.');
    }
}

/**
 * Cleans up scripts and instances before a page transition.
 * @param {object} data - Barba transition data object.
 */
function cleanupPageScripts(data) {
    if (!data || !data.current) return;
    if (DEBUG_MODE) console.log('Barba: Cleaning up scripts from old page:', data.current.namespace);
    // We no longer destroy Lenis on every transition.
    // destroyScrollManager();
    destroyScrollEffects(); // Disconnects IntersectionObservers for scroll effects
    destroyVideoPlayers(data.current.container); // Clean up video players to prevent memory leaks
    destroySlideshows(DEBUG_MODE); // Destroy all swiper instances
    destroyParallax(); // Disconnects observers and cleans up parallax elements
    destroyModals(); // Resets modal state for clean re-initialization
    //destroyPropertiesMap(); // Clean up properties map and ALM listeners
    destroyCountUp(); // Clean up CountUp observers
    destroyHeroTaglines(); // Clean up Hero Taglines
    destroyHeroScrollOpacity(); // Clean up Hero Scroll Opacity
    destroyAccordions(); // Clean up active accordion references
    destroyTabs(); // Clean up tabs
    destroyStickyHeader(); // Clean up sticky header listener
    destroyScrollProgressIndicators(); // Clean up scroll progress listener
    destroyScrollPercentElements();
    destroyScrollVarElements();
    destroyScrollToTop(lenisInstance); // Clean up scroll to top listener
    destroyMenuBg(); // Clean up menu bg scroll listener
    if (footerSpacerInstance) {
        footerSpacerInstance.destroy();
        footerSpacerInstance = null;
    }

    // IMPORTANT: Destroy other plugin instances if they need manual cleanup.
    // Examples:
    // if (jQuery && jQuery.fn.slick && jQuery('.my-slider', data.current.container).hasClass('slick-initialized')) {
    //   jQuery('.my-slider', data.current.container).slick('unslick');
    // }
}

/*
function initializeRoundedCornersSiblings(container = document) {
    const sectionals = container.querySelectorAll('.sectional');
    sectionals.forEach((sec) => {
        const next = sec.nextElementSibling;
        if (next && next.classList.contains('sectional')) {
            if (next.classList.contains('has-rounded-corners-anim') || next.querySelector('.layout__cards--19')) {
                sec.classList.add('has-rounded-corners-sibling');
            }
        }
    });
}
*/

document.addEventListener('DOMContentLoaded', () => {
    // --- Initialize Persistent Services ---

    // Initialize Lenis once and for all.
    // Adjust the `lerp` value to change scroll speed. Lower is smoother/slower, higher is faster. Default is 0.1.
    lenisInstance = initializeScrollManager({
        lerp: 0.075
    });
    if (lenisInstance) {
        initializeLenisAnchorScrolling(lenisInstance);
        initScrollPercentElements(lenisInstance);
        initScrollVarElements(lenisInstance);
    }

    // Initialize DOM listeners that should only run once.
    initializeDomLoadListeners();
    // Initialize interactive header elements. Since the header is persistent and not
    // replaced by Barba, this only needs to run once on initial page load.
    initializeInteractiveHeader();
    initializeMobileMenu();

    // Initialize rounded corners sibling indicators
    //initializeRoundedCornersSiblings();

    // Initialize the image zoom dialog listeners.
    initializeImageZoom();

    // Initialize all modal functionality, including click triggers and auto-triggers.
    initializeModals(DEBUG_MODE);

    // Initialize the video popup handler.
    initializeVideoPopupHandler();

    // Initialize global event listeners for resizing videos.
    initializeVideoCoverResizerListeners('.video-container');

    // Initialize faux select boxes (covers persistent header and initial page content)
    initializeFauxSelects();

    // Initialize accordions
    initializeAccordions();

    // Initialize tabs
    initializeTabs();

    // Initialize read reveal
    initializeReadReveal();
    initializeReadRevealListeners();

    // Initialize quick search logic
    //initializeQuickSearch();

    // Initialize ALM Features
    initializeAlmFeatures();
    
    // Initialize FAQs archive logic
    initializeFaqs();

    // Initialize Scroll to Top button
    initializeScrollToTop(lenisInstance);

    // Initialize Menu Background scroll progress
    initializeMenuBg(lenisInstance);

    // Initialize Obfuscated Links (Global/Header)
    initializeObfuscatedLinks();

    // Initial Page Load: Monitor Media Loading
    // We manually simulate the loader flow for the first hit
    if (!document.documentElement.classList.contains('page-ready')) {
        monitorMediaLoading(document, DEBUG_MODE).then(() => {
             document.documentElement.classList.add('page-ready');
             // If there was an initial .is-loading class (php side), remove it here
             document.body.classList.remove('is-loading');
        });
    }

    // ALM Reset Button Animation
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('.alm-filters-container button[type="reset"]');
        if (btn) {
            btn.classList.add('animate-spin');
            btn.addEventListener('animationend', () => {
                btn.classList.remove('animate-spin');
            }, { once: true });
        }
    });

    // --- Barba.js Setup ---
    // Initialize Barba immediately to ensure it captures the initial history state
    // before other scripts (like ALM Filters running on window.load) interfere.

    // Optional: Use @barba/css for class-based transitions
    // barba.use(barbaCss);

    barba.init({
        sync: true, // Syncs body classes and page title from the fetched page
        debug: DEBUG_MODE,
        timeout: 7000, // Default timeout for transitions
        prevent: barbaPreventHandler,
        transitions: [{
            name: 'default',
            once(data) {
                if (DEBUG_MODE) {
                    console.log('[Barba Debug] once: once event');
                }
                initializePageScripts(data); // Initialize scripts on first page load
                // Manually track the initial page view since automatic tracking will be disabled.
                trackPageView(false, DEBUG_MODE);
                //document.documentElement.classList.add('page-ready');
            },
            async leave(data) {
                // Dispatch a custom event to notify other modules (like the header) that a page transition is starting.
                document.dispatchEvent(new CustomEvent('barba-leave-start', { detail: data }));

                document.documentElement.classList.remove('page-ready');

                // Hide the current content immediately to prevent it from being visible during the transition.
                if (data.current.container) {
                    data.current.container.style.display = 'none';
                }

                // Create a promise that resolves after a minimum duration (e.g., 500ms)
                const minDuration = new Promise(resolve => {
                    setTimeout(resolve, 500);
                });

                // Add class to body to show the page loader
                document.body.classList.add('is-loading');
                resetPageLoaderProgress(); // Reset bar to 0%
                
                // Simulate a little progress to show activity during the request
                setTimeout(() => updatePageLoaderProgress(20), 100);

                await minDuration; // Wait for at least 500ms

                resetAjaxLoadMore(DEBUG_MODE);

                cleanupPageScripts(data);

            },
            beforeEnter(data) {
                if (DEBUG_MODE) {
                    console.log('[Barba Debug] beforeEnter: document.body.className AT START of beforeEnter:', document.body.className);
                    console.log('[Barba Debug] beforeEnter: Next page raw HTML received by Barba:', data.next.html.substring(0, 1000) + '...'); // Log first 1000 chars
                }

                // Barba's `sync: true` handles body classes. These manual syncs ensure
                // assets that live outside the [data-barba="container"] are updated.
                syncManualBodyClasses(data.next.html, DEBUG_MODE);
                syncPageStylesheets(data.next.html, DEBUG_MODE); // Sync <head> stylesheet assets (e.g. Gravity Forms CSS) enqueued only on the next page
                syncPageStyles(data.next.html, DEBUG_MODE); // Sync styles before content is visible
                syncPageModals(data.next.html, DEBUG_MODE); // Sync modals before content is visible
                syncPageScriptsByLocation(data.next.html, ['head', 'body-start'], DEBUG_MODE); // Sync early scripts

                // Manually refresh Gravity Forms scripts
                refreshGravityForms(data.next.html, DEBUG_MODE);

                // Reset animation state on all container elements so they enter smoothly
                resetContainerAnimations(data.next.container);

                // Reset scroll position. If Lenis is active, use it.
                if (lenisInstance) {
                    lenisInstance.scrollTo(0, { immediate: true, force: true });
                } else {
                    window.scrollTo(0, 0);
                }

            },
            enter(data) {
                if (DEBUG_MODE) console.log('[Barba Debug] enter: document.body.className AT START of enter:', document.body.className);
                // The new content is already in the DOM. 
                // We DON'T remove is-loading yet. We wait for afterEnter -> monitorMedia.
                if (DEBUG_MODE) console.log('[Barba Debug] enter: document.body.className AT END of enter:', document.body.className);
            },
            async afterEnter(data) {
                if (DEBUG_MODE) {
                    console.log('[Barba Debug] afterEnter: document.body.className AT START of afterEnter (BEFORE initializePageScripts):', document.body.className);
                }

                // Sync scripts intended for the end of the body, like page-specific data or analytics, before the page is initialized.
                syncPageScriptsByLocation(data.next.html, ['body-end'], DEBUG_MODE);

                

                initializePageScripts(data); // Initialize scripts for the newly entered page

                if (DEBUG_MODE) {
                    console.log('[Barba Debug] afterEnter: document.body.className AFTER initializePageScripts:', document.body.className);
                }
                // Track page view with Google Analytics for the new page
                trackPageView(data, DEBUG_MODE);

                // Wait for above-the-fold media to load
                await monitorMediaLoading(data.next.container, DEBUG_MODE);

                // Remove the loading class to hide the page loader
                // This will start the fade-out animation.
                document.body.classList.remove('is-loading');
                document.documentElement.classList.add('page-ready');

                reinitializeAjaxLoadMore(data.next.container, DEBUG_MODE);

                // Wait for the fade-out animation (2000ms) to complete before Barba finishes.
                // This ensures the loader is fully gone before the user can interact with the new page.
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }],

        // Optional: Define views for page-specific JavaScript logic
        // views: [
        //   {
        //     namespace: 'home', // Corresponds to data-barba-namespace="home"
        //     afterEnter(data) { /* Home-specific scripts, e.g., init home page slider */ }
        //   },
        //   {
        //     namespace: 'contact-page', // Example namespace from your PHP
        //     afterEnter(data) { /* Initialize contact form scripts */ }
        //   }
        // ]
    });

    // Load fonts (visual only)
    loadWebFonts(selfHostedFontConfig, adobeFontConfig);
});

// The `beforeunload` event is a good place to destroy persistent instances like Lenis
// to ensure a clean state if the user navigates away from the site entirely.
window.addEventListener('beforeunload', () => {
  destroyScrollManager();
});


/*
├── app.js                     // Main entry point, orchestrates initializations
├── core/                      // Core utilities, global initializers, polyfills
│   ├── animation-initializer.js // (was layout-animations.js - for AOS, simpleParallax init)
│   ├── console-support.js
│   ├── css-viewport-height.js
│   ├── is-touch.js
│   ├── media-helpers.js       // (for object-fit-cover.js, html-videos.js logic)
│   ├── obfuscate-links.js
│   ├── polyfills.js           // (for input-placeholder-polyfill, objectFitPolyfill global call)
│   ├── prevent-default-class-helper.js
│   ├── scroll-manager.js      // (was smooth-page-scroll.js - Lenis, global scroll logic)
│   ├── scroll-utils.js        // (for scroll-percentage.js)
│   ├── url-utils.js           // (for querystring-dragging.js)
│   └── web-fonts.js
├── ui/                        // UI components and interactions
│   ├── accordion.js
│   ├── header/                // Header-specific UI components
│   │   ├── interactive-header.js // Handles dropdowns and search panel
│   │   ├── enquire-button.js  // (was header-enquire-button.js)
│   │   ├── mobile-menu.js     // (was header-mobile-menu.js)
│   │   └── sticky.js          // (was header-sticky.js)
│   ├── image-compare.js
│   ├── link-buttons.js
│   ├── modal-manager.js       // (was modals.js - MicroModal init, exit-intent logic)
│   ├── slideshow-manager.js   // (was slideshow.js - Swiper specific logic)
│   ├── sticky-drawer.js
│   ├── tabs.js
│   └── video/                 // UI components related to video presentation
│       ├── modal-loader.js    // (was modal-video-loader.js)
│       └── slideshow-loader.js// (was slideshow-video-loader.js)
├── features/                  // Distinct site features, often combining UI and logic
│   ├── calendar.js            // (from custom/)
│   ├── careers.js             // (from custom/)
│   ├── count-up.js            // (from custom/)
│   ├── faqs.js
│   ├── refine-select-filters.js
│   ├── scroll-window-effects.js // (was scroll-window.js and its animations)
│   └── url-jump-select.js
├── integrations/              // Third-party service/library integrations
│   ├── ajax-load-more-init.js // (was ajax-load-more.js, assuming it's an init script)
│   ├── google-maps-manager.js // (was google-maps.js)
│   ├── gravityforms-helpers.js// (was gravityforms-fileupload.js)
│   └── iframe-resizer-init.js // (was iframe-resize.js)
└── vendor/                    // (If any 3rd party libs are manually included and not via npm)
    └── ...
*/